/**
 * AddOperatorModal — Task 25.3 (DEC-114). Manually add an operator not in OCC/RRC.
 */
import * as React from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { FormRow, fieldStyle, labelStyle, haulIdPreview } from '../../ui/bits';
import { useCreateOperator } from './hooks';
import { ABBREVIATION_RE, ABBREVIATION_RULE_TEXT } from '@lodex-pro/shared/abbreviation';

export interface AddOperatorModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onCreated: (id: string) => void;
}

export function AddOperatorModal({
  open,
  onOpenChange,
  onCreated,
}: AddOperatorModalProps): React.JSX.Element {
  const [name, setName] = React.useState('');
  const [state, setState] = React.useState('');
  const [abbrev, setAbbrev] = React.useState('');
  const [isProducer, setIsProducer] = React.useState(true);
  const [isDisposal, setIsDisposal] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setName('');
    setState('');
    setAbbrev('');
    setIsProducer(true);
    setIsDisposal(false);
    setError(null);
  }, [open]);

  const create = useCreateOperator((id) => {
    toast.success('Operator added');
    onOpenChange(false);
    onCreated(id);
  });

  const save = (): void => {
    if (name.trim() === '') {
      setError('Operator name is required.');
      return;
    }
    if (abbrev !== '' && !ABBREVIATION_RE.test(abbrev.trim())) {
      setError(ABBREVIATION_RULE_TEXT);
      return;
    }
    setError(null);
    create.mutate(
      {
        name: name.trim(),
        ...(state.trim() !== '' && { state: state.trim().toUpperCase() }),
        isProducer,
        isDisposal,
        ...(abbrev.trim() !== '' && { abbreviation: abbrev.trim().toUpperCase() }),
      },
      {
        onError: () => setError('Could not add operator — the abbreviation may already be taken.'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add operator</DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <FormRow label="Operator name" htmlFor="op-name" required>
            <input
              id="op-name"
              value={name}
              onChange={(e): void => setName(e.target.value)}
              placeholder="e.g. Devon Energy"
              style={fieldStyle}
            />
          </FormRow>
          <FormRow label="State" htmlFor="op-state" hint="Two-letter code (OK, TX, …).">
            <input
              id="op-state"
              value={state}
              maxLength={2}
              onChange={(e): void => setState(e.target.value.toUpperCase())}
              placeholder="OK"
              style={{ ...fieldStyle, textTransform: 'uppercase', maxWidth: '100px' }}
            />
          </FormRow>
          <FormRow
            label="Abbreviation (letters only, 2–4)"
            htmlFor="op-abbrev"
            hint={`Tickets will look like: ${haulIdPreview(abbrev)}`}
          >
            <input
              id="op-abbrev"
              value={abbrev}
              maxLength={4}
              onChange={(e): void => setAbbrev(e.target.value.toUpperCase())}
              placeholder="DVN"
              style={{ ...fieldStyle, textTransform: 'uppercase', maxWidth: '140px' }}
            />
          </FormRow>
          <div style={{ display: 'flex', gap: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isProducer}
                onChange={(e): void => setIsProducer(e.target.checked)}
              />
              <span style={{ ...labelStyle, marginBottom: 0, textTransform: 'none' }}>
                Producer
              </span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isDisposal}
                onChange={(e): void => setIsDisposal(e.target.checked)}
              />
              <span style={{ ...labelStyle, marginBottom: 0, textTransform: 'none' }}>
                Disposal
              </span>
            </label>
          </div>
          {error !== null && (
            <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={(): void => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={create.isPending}
            onClick={save}
            style={{ backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }}
          >
            {create.isPending ? 'Adding…' : 'Add Operator'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
